import type {
    BroadcastConfig,
    IvsRealTimePlayerConfig,
    PlayerConfigCommon,
    StreamingProvider,
} from '$lib/services/streaming/types';

import { StreamProvider, StreamStatus } from '@shared/types/room';

export default class IvsRealTime implements StreamingProvider {
    private broadcastConfig?: BroadcastConfig;
    private broadcaster?: InstanceType<typeof import('./broadcaster').IvsBroadcaster>;
    private player?: InstanceType<typeof import('./player').IvsRealTimePlayer>;

    public getType() {
        return StreamProvider.IvsRealTime;
    }

    private async ensureBroadcaster() {
        if (!this.broadcaster) {
            const { IvsBroadcaster } = await import('./broadcaster');
            this.broadcaster = new IvsBroadcaster();
        }
    }

    private async ensurePlayer() {
        if (!this.player) {
            const { IvsRealTimePlayer } = await import('./player');
            this.player = new IvsRealTimePlayer();
        }
    }

    public async startBroadcast(config: BroadcastConfig) {
        await this.ensureBroadcaster();
        this.broadcastConfig = config;
        return this.broadcaster!.startBroadcast(config);
    }

    public async stopBroadcast() {
        await this.ensureBroadcaster();
        return this.broadcaster!.stopBroadcast();
    }

    public async startDelivery(config: IvsRealTimePlayerConfig & PlayerConfigCommon) {
        await this.ensurePlayer();
        return this.player!.startDelivery(config);
    }

    public async stopDelivery() {
        await this.ensurePlayer();
        return this.player!.stopDelivery();
    }

    public async destroy() {
        await this.stopDelivery();
        await this.stopBroadcast();
    }

    public async restartBroadcast(byPlayerHealth = false) {
        if (!byPlayerHealth) {
            await this.stopBroadcast();
            if (this.broadcastConfig) {
                await this.startBroadcast(this.broadcastConfig);
            }
        } else {
            this.broadcastConfig?.onEvent(StreamStatus.PeerConnectionEstablished);
        }
    }
}
